import { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RefreshCcw } from 'lucide-react';
import { useTranslate } from 'sbx-crm-menu-ui';

interface ColorPickerProps {
    value: string | undefined;
    oldValue: string | undefined;
    onChange?: (color: string) => void;
    label?: string;
    name: string;
    placeholder?: string;
    disabled?: boolean;
    classNameContent?: string;
    classNameLabel?: string;
    classNameInput?: string;
    required?: boolean;
}

export function hexToRgb(hex: string) {
    hex = hex.replace(/^#/, '');
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
}

export function rgbaToRgb(rgba: string) {
    if (typeof rgba !== 'string' || !rgba.includes('rgb')) {
        return { r: 0, g: 0, b: 0 };
    }

    const matches = rgba.match(/\d+/g);
    if (!matches || matches.length < 3) {
        return { r: 0, g: 0, b: 0 };
    }

    return {
        r: parseInt(matches[0]),
        g: parseInt(matches[1]),
        b: parseInt(matches[2]),
    };
}

export function getAlpha(rgba: string) {
    if (typeof rgba !== 'string' || !rgba.includes('rgba')) {
        return 1;
    }

    const matches = rgba.match(/[\d.]+/g);
    if (!matches || matches.length < 4) {
        return 1;
    }

    return parseFloat(matches[3]);
}

function colorInputToHex(color: string) {
    const colorComponents = rgbaToRgb(color);
    if (!colorComponents.r && !colorComponents.g && !colorComponents.b) {
        return '';
    }
    return `#${colorComponents.r.toString(16).padStart(2, '0')}${colorComponents.g
        .toString(16)
        .padStart(2, '0')}${colorComponents.b.toString(16).padStart(2, '0')}`;
}

function getRgbColors(color: string) {
    let colors = {};
    if (color?.includes('rgb')) {
        colors = rgbaToRgb(color);
    } else {
        colors = hexToRgb(color);
    }
    return colors as any;
}

export function ColorPickerTransparencyComponent({
    value,
    oldValue,
    onChange = () => {},
    label,
    placeholder,
    disabled,
    classNameContent = '',
    classNameLabel = '',
    classNameInput = '',
    required,
}: ColorPickerProps) {
    const [colorComponents, setColorComponents] = useState('');
    const [opacityBck, setOpacityBck] = useState(1);
    const { t: c } = useTranslate('common');

    const onChangeColor = (colorString: string, callOnChange = false) => {
        if (!colorString || typeof colorString !== 'string') return;

        const { r, g, b } = getRgbColors(colorString);
        const coloRGBA = `rgba(${r}, ${g}, ${b}, ${opacityBck})`;

        setColorComponents(coloRGBA);
        if (callOnChange) onChange(coloRGBA);
    };

    useEffect(() => {
        if (!value) {
            setColorComponents('');
            setOpacityBck(1);
            return;
        }
        onChangeColor(value);
        setOpacityBck(getAlpha(value));
    }, [value]);

    const handleColorChange = (color: string) => {
        onChangeColor(color, true);
    };

    const handleColorAllChange = (color: string) => {
        const { r, g, b } = getRgbColors(color);
        const alpha = getAlpha(color);
        const coloRGBA = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        setColorComponents(coloRGBA);
        onChange(coloRGBA);
    };

    const handleAlphaChange = (alpha: number) => {
        setOpacityBck(alpha);
        const colorT = rgbaToRgb(colorComponents);
        const colorString = `rgba(${colorT.r}, ${colorT.g}, ${colorT.b}, ${alpha})`;
        setColorComponents(colorString);
        onChange(colorString);
    };

    const handleColorInputChange = (inputValue: string) => {
        try {
            onChangeColor(inputValue, true);
        } catch {
            onChange(inputValue);
        }
    };

    return (
        <div className={`${classNameContent}`}>
            {label && <Label className={classNameLabel}>{label}</Label>}
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <div
                        className="w-16 h-10 border rounded cursor-pointer relative overflow-hidden"
                        style={{ backgroundColor: value }}
                    >
                        <input
                            type="color"
                            value={colorInputToHex(colorComponents)}
                            onChange={(e) => handleColorChange(e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={disabled}
                        />
                    </div>
                    <div className="relative flex-1">
                        <Input
                            value={value}
                            onChange={(e) => handleColorInputChange(e.target.value)}
                            placeholder={placeholder || '#3b82f6 or rgba(59, 130, 246, 0.8)'}
                            className={`${classNameInput}`}
                            disabled={disabled}
                            required={required}
                        />
                        {oldValue && oldValue !== value && (
                            <RefreshCcw
                                onClick={() => {
                                    handleColorAllChange(oldValue);
                                }}
                                className="cursor-pointer absolute top-1/2 transition-all -translate-y-1/2 right-2 w-4 h-4 hover:rotate-45 text-primary"
                            />
                        )}
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="alpha" className="text-sm">
                            {c('transparency')}
                        </Label>
                        <span className="text-sm text-muted-foreground">{Math.round(opacityBck * 100)}%</span>
                    </div>
                    <input
                        id="alpha"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={opacityBck}
                        disabled={disabled}
                        onChange={(e) => handleAlphaChange(Number.parseFloat(e.target.value))}
                        style={{
                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${
                                opacityBck * 100
                            }%, #e5e7eb ${opacityBck * 100}%, #e5e7eb 100%)`,
                        }}
                        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
            </div>
        </div>
    );
}
